/**
 * A lead's email and quote-link timeline (`GET /v1/orgs/{org}/public-quotes/{id}/activity`) in words, for the
 * QuoteOfferCard (pure, tested with node --test). Delivery status comes from Azure Communication Services delivery
 * reports; there is no open tracking, so "Delivered" is the most an email says. Views are the homeowner's opens of
 * the quote page (not bots, link scanners or the company's own people). The team's own opens are previews, shown
 * apart and never counted: by a signed-in member (certain) or from the team's network (an IP a member used for the
 * leads in the last 30 days: usually the team, possibly a homeowner on the same network).
 */
import type { MessageKey } from "../i18n/keys";
import { intlLocale } from "./format.ts";
import type { Tr } from "./tr.ts";

export type EmailKind =
  | "proposal" | "quote_confirmed" | "proposal_resent" | "quote_updated"
  | "lead_notification" | "lead_quote_updated" | "renewal_request" | "other";
export type EmailStatus =
  | "queued" | "sent" | "delivered" | "expanded" | "bounced" | "suppressed" | "quarantined" | "filtered_spam" | "failed";
export type EmailLike = {
  id: string;
  kind: EmailKind;
  audience: "homeowner" | "company";
  to: string[];
  created_at: string;
  sent_at?: string | null;
  status: EmailStatus;
  status_at?: string | null;
  detail?: string | null;
  problem: boolean;
};
export type ViewsLike = {
  view_count: number;
  first_viewed_at?: string | null;
  last_viewed_at?: string | null;
  member_preview_count?: number;
  member_last_previewed_at?: string | null;
  network_preview_count?: number;
  network_last_previewed_at?: string | null;
};
export type PreviewKind = "member" | "network";

export const PREVIEW_LABELS: Record<PreviewKind, MessageKey> = {
  member: "designs.activity.preview.member",
  network: "designs.activity.preview.network",
};
export const PREVIEW_HINTS: Record<PreviewKind, MessageKey> = {
  member: "designs.activity.previewHint.member",
  network: "designs.activity.previewHint.network",
};

/** "3 opens" under a preview line ("" for a single open); the line itself shows the latest open's time. */
export function previewSub(kind: PreviewKind, v: ViewsLike, t: Tr): string {
  const count = (kind === "member" ? v.member_preview_count : v.network_preview_count) ?? 0;
  if (count <= 1) return "";
  return t("designs.activity.previewOpens", { count });
}

export const KIND_LABELS: Record<EmailKind, MessageKey> = {
  proposal: "designs.activity.kind.proposal",
  quote_confirmed: "designs.activity.kind.quoteConfirmed",
  proposal_resent: "designs.activity.kind.proposalResent",
  quote_updated: "designs.activity.kind.quoteUpdated",
  lead_notification: "designs.activity.kind.leadNotification",
  lead_quote_updated: "designs.activity.kind.leadQuoteUpdated",
  renewal_request: "designs.activity.kind.renewalRequest",
  other: "designs.activity.kind.other",
};

export const STATUS_LABELS: Record<EmailStatus, MessageKey> = {
  queued: "designs.activity.status.queued",
  sent: "designs.activity.status.sent",
  delivered: "designs.activity.status.delivered",
  expanded: "designs.activity.status.expanded",
  bounced: "designs.activity.status.bounced",
  suppressed: "designs.activity.status.suppressed",
  quarantined: "designs.activity.status.quarantined",
  filtered_spam: "designs.activity.status.filteredSpam",
  failed: "designs.activity.status.failed",
};

/** Plain-language meaning of a problem status, for the warning. */
export const PROBLEM_HINTS: Partial<Record<EmailStatus, MessageKey>> = {
  bounced: "designs.activity.problem.bounced",
  suppressed: "designs.activity.problem.suppressed",
  quarantined: "designs.activity.problem.quarantined",
  filtered_spam: "designs.activity.problem.filteredSpam",
  failed: "designs.activity.problem.failed",
};

export type Tone = "ok" | "warn" | "danger" | "info" | undefined;

export function statusTone(status: EmailStatus): Tone {
  if (status === "delivered" || status === "expanded") return "ok";
  if (status === "queued" || status === "sent") return undefined;
  if (status === "quarantined" || status === "filtered_spam") return "warn";
  return "danger";
}

/** "10:02" today, "Oct 8, 10:02" otherwise (the dashboard's language, the viewer's time zone). */
export function when(value: string | null | undefined, now: Date = new Date(), locale: string | undefined = intlLocale()): string {
  if (!value) return "";
  const d = new Date(value);
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString(locale, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** "Quote email · Delivered 10:02" */
export function emailLine(e: EmailLike, t: Tr, now?: Date, locale?: string): string {
  const at = when(e.status_at ?? e.sent_at ?? e.created_at, now, locale);
  const values = { kind: t(KIND_LABELS[e.kind]), status: t(STATUS_LABELS[e.status]), at };
  return at ? t("designs.activity.emailLine", values) : t("designs.activity.emailLineNoTime", values);
}

/** "Quote page viewed 10:16 (3 views)", "Quote page not opened yet". */
export function viewsLine(v: ViewsLike, t: Tr, now?: Date, locale?: string): string {
  if (!v.view_count) return t("designs.activity.viewsNone");
  const first = when(v.first_viewed_at, now, locale);
  if (v.view_count > 1 && v.last_viewed_at)
    return t("designs.activity.viewsLast", { first, count: v.view_count, last: when(v.last_viewed_at, now, locale) });
  return t("designs.activity.views", { first, count: v.view_count });
}

/** The homeowner email to warn about: the latest one sent to the homeowner, when it did not arrive (an earlier
 * problem followed by a delivered email needs no warning). */
export function homeownerWarning(emails: EmailLike[]): EmailLike | null {
  const own = emails.filter((e) => e.audience === "homeowner");
  const latest = own.reduce<EmailLike | null>((a, e) => (!a || new Date(e.created_at) >= new Date(a.created_at) ? e : a), null);
  return latest && latest.problem ? latest : null;
}

/** Whether to poll: an email is still on its way (queued, or sent less than 30 min ago without a report). */
export function inFlight(emails: EmailLike[], now: Date = new Date()): boolean {
  return emails.some(
    (e) => e.status === "queued" || (e.status === "sent" && now.getTime() - new Date(e.sent_at ?? e.created_at).getTime() < 30 * 60_000),
  );
}

/** The timeline in time order: each email, the views (placed at the first view) and the team's previews (placed at
 * their latest open, which is the time they show). */
export type Entry = { key: string; at: string; email?: EmailLike; views?: ViewsLike; preview?: PreviewKind };
export function entries(emails: EmailLike[], views: ViewsLike): Entry[] {
  const out: Entry[] = emails.map((e) => ({ key: e.id, at: e.sent_at ?? e.created_at, email: e }));
  if (views.view_count && views.first_viewed_at) out.push({ key: "views", at: views.first_viewed_at, views });
  if (views.member_preview_count && views.member_last_previewed_at)
    out.push({ key: "preview-member", at: views.member_last_previewed_at, views, preview: "member" });
  if (views.network_preview_count && views.network_last_previewed_at)
    out.push({ key: "preview-network", at: views.network_last_previewed_at, views, preview: "network" });
  return out.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}
